import { trimSpaces, trimSpacesSQL } from './trimSpaces';
import { extractJsonFieldSql } from './extractJsonFieldSql';
import { OperationsEnum } from '../enums/OperationsEnum';
import { AttributesFilterInputInterface } from '../interfaces/AttributesFilterInputInterface';
import { AttributeNameValuesPairInterface } from '../interfaces/AttributeNameValuesPairInterface';

const isObjectWithKeys = (item: Record<string, any>) => Object.keys(item).length > 0;
const isAttributeFilterInput = (arg: any): arg is AttributesFilterInputInterface => {
  return !!arg && (arg.values !== undefined || arg.or !== undefined || arg.and !== undefined);
};

export function processLocalizedFilterValue(
  params: string[],
  { values, name, op }: AttributeNameValuesPairInterface,
  rawName: string,
): string {
  if (values === null) {
    switch (op) {
      case OperationsEnum.ne:
      case OperationsEnum.nin: {
        return `${extractJsonFieldSql('attributes', `$.${name}.${rawName}`)} IS NOT NULL`;
      }
      case OperationsEnum.like:
      case OperationsEnum.eq:
      case OperationsEnum.in:
      default: {
        return `${extractJsonFieldSql('attributes', `$.${name}.${rawName}`)} IS NULL`;
      }
    }
  }
  switch (op) {
    case OperationsEnum.like: {
      const where = values.map((v) => {
        params.push(v);
        return `${extractJsonFieldSql('attributes', `$.${name}.${rawName}`)} LIKE ?`;
      }).join(' OR ');
      return values.length > 1 ? `(${where})` : where;
    }
    case OperationsEnum.ne:
    case OperationsEnum.nin: {
      const places = values.map((value) => {
        params.push(value);
        return '?';
      }).join(',')
      return `${extractJsonFieldSql('attributes', `$.${name}.${rawName}`)} NOT IN (${places})`;
    }
    case OperationsEnum.eq:
    case OperationsEnum.in:
    default: {
      const places = values.map((value) => {
        params.push(value);
        return '?';
      }).join(',')
      return `${extractJsonFieldSql('attributes', `$.${name}.${rawName}`)} IN (${places})`;
    }
  }
}

function processAttributesFilterInput(
  params: string[],
  filter: AttributesFilterInputInterface,
  rawName: string,
): string {
  const out: string[] = [];
  const or = filter.or || [];
  if (or.length > 0) {
    const where = or.map((item) => processAttributesFilterInput(params, item, rawName)).join(' OR ');
      out.push(or.length > 1 ? `(${where})` : where);
  }
  const and = filter.and || [];
  if (and.length > 0) {
      out.push(and.map((item) => processAttributesFilterInput(params, item, rawName)).join(' AND '));
  }
  (filter.values || [])
    .forEach((item) => {
        if (item.name === 'label') {
          switch (item.op) {
            case OperationsEnum.like: {
              if (item.values === null) {
                out.push(`${item.name} IS NULL`);
                break;
              }
              const where = item.values.map((value) => `%%${value}%%`).reduce((acc, value) => {
                params.push(value);
                params.push(trimSpaces(value));
                return [
                  ...acc,
                  `${item.name} LIKE ?`,
                  `${trimSpacesSQL(item.name)} LIKE ?`,
                ];
              }, [] as string[]).join(' OR ');
              out.push(item.values.length > 1 ? `(${where})`: where);
              break;
            }
            case OperationsEnum.ne:
            case OperationsEnum.nin: {
              if (item.values === null) {
                out.push(`${item.name} IS NOT NULL`);
                break;
              }
              const places = item.values.map((value) => {
                params.push(value);
                return '?';
              }).join(',');
              out.push(`${item.name} NOT IN (${places})`);
              break;
            }
            case OperationsEnum.eq:
            case OperationsEnum.in:
            default: {
              if (item.values === null) {
                out.push(`${item.name} IS NULL`);
                break;
              }
              const places = item.values.map((value) => {
                params.push(value);
                return '?';
              }).join(',');
              out.push(`${item.name} IN (${places})`);
              break;
            }
          }
          return;
        }
        out.push(processLocalizedFilterValue(params, item, rawName));
    });
  return `(${out.join(' AND ')})`;
}

const processExclude = (filter: AttributesFilterInputInterface, exclude: string[]): AttributesFilterInputInterface | {} => {
  const or = (filter.or || []).map((inner) => processExclude(inner, exclude)).filter(isObjectWithKeys);
  const and = (filter.and || []).map((inner) => processExclude(inner, exclude)).filter(isObjectWithKeys);
  const values = (filter.values || []).filter((item) => !exclude.includes(item.name));
  return {
    ...(values.length > 0 ? { values } : {}),
    ...(or.length > 0 ? { or } : {}),
    ...(and.length > 0 ? { and } : {}),
  };
}

export function buildWhereFromFilters(
  filter: AttributesFilterInputInterface,
  exclude: string[] = [],
  rawName: string,
): [string, string[]] {
  const filterExcluded = processExclude(filter, exclude);
  if (isAttributeFilterInput(filterExcluded)) {
      const params: string[] = []
      return [processAttributesFilterInput(params, filterExcluded, rawName), params];
  } else {
    return ['', []];
  }
}
